import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';

import { Button, LoadingIndicator } from '@zendesk/forethought-elements';
import {
  useGetIntentsQueryWithProduct,
  useIsFeatureFlagEnabled,
} from 'src/hooks/hooks';
import {
  getWorkflowBuilderUrl,
  openTab,
} from 'src/pages/intent-conversation-analytics/helpers';
import { useGetParentWorkflowIdQuery } from 'src/services/workflow/workflowApi';
import {
  AllConversationChannel,
  IntentData,
} from 'src/types/workflowBuilderAPITypes';
import { getEffectiveWorkflowMode } from 'src/utils/solve/intentUtils';

const matchesWorkflowId = (intent: IntentData, workflowId: string): boolean =>
  [
    intent.intent_workflow_id,
    intent.api_workflow_id,
    intent.interactive_email_workflow_id,
    intent.playbook_workflow_id,
    intent.slack_workflow_id,
    intent.voice_workflow_id,
    intent.web_call_workflow_id,
  ].includes(workflowId);

export const DeepLink = ({
  channel,
  intentDefinitionId,
  linkText,
  stepId,
  workflowId,
}: {
  channel: AllConversationChannel | null;
  intentDefinitionId?: string;
  linkText?: string;
  stepId?: string | null;
  workflowId?: string | null;
}) => {
  const { t } = useTranslation('events');
  const { palette } = useTheme();
  const { data, isLoading } = useGetIntentsQueryWithProduct();
  const isClassicWorkflowsDeprecated = useIsFeatureFlagEnabled(
    'classic_workflows_deprecated',
  );

  const { data: parentData, isLoading: isLoadingParent } =
    useGetParentWorkflowIdQuery(
      { intentWorkflowId: workflowId ?? '' },
      { skip: !workflowId || !stepId },
    );

  if (!workflowId || !stepId) {
    return null;
  }

  const parentWorkflowId = parentData?.parent_autoflow_intent_workflow_id;
  const workflowIntent =
    (intentDefinitionId
      ? data?.intents.find(
          intent => intent.intent_definition_id === intentDefinitionId,
        )
      : undefined) ??
    data?.intents.find(intent => matchesWorkflowId(intent, workflowId)) ??
    (parentWorkflowId
      ? data?.intents.find(intent =>
          matchesWorkflowId(intent, parentWorkflowId),
        )
      : undefined);
  const intentId =
    intentDefinitionId ?? workflowIntent?.intent_definition_id ?? '';
  const workflowName = workflowIntent?.intent_name?.trim();

  // Use parent workflow ID if this is a building block, otherwise use the workflow ID
  const targetWorkflowId = parentWorkflowId || workflowId;

  const targetURL = getWorkflowBuilderUrl({
    channel: channel || 'widget',
    intentDefinitionId: intentId || '',
    intentWorkflowId: targetWorkflowId,
    mode: getEffectiveWorkflowMode(
      intentId,
      Boolean(parentWorkflowId),
      isClassicWorkflowsDeprecated,
    ),
    stepId: stepId,
    // Only pass stepWorkflowId if this is a building block (has a parent)
    stepWorkflowId: parentWorkflowId ? workflowId : undefined,
  });

  if (isLoading || isLoadingParent) {
    return <LoadingIndicator color={palette.colors.black} size='large' />;
  }

  return (
    <Button
      onClick={() => {
        openTab(targetURL);
      }}
      variant='main'
    >
      {linkText ??
        (workflowName
          ? t('deepLink.namedLinkText', { workflowName })
          : t('deepLink.linkText'))}
    </Button>
  );
};
